import React, { useMemo } from 'react';
import { formaDespacho as formaDespachoList, etapasPostVentaList } from '../../../data/listasDesplegables';

/**
 * SectionAdicional - Componente modular para el Despacho, Próxima Refacturación 
 * y el Registro Inicial de Postventa/Seguimiento.
 * Optimizado con cálculos en memoria (useMemo) y diseño nativo en Tailwind CSS.
 */
const SectionAdicional = ({ state, actions }) => {
    // Extraemos lo necesario de los objetos agrupados
    const { formData, dbMasters } = state;
    const { handleChange, setFormData } = actions;
    
    // --- ORDENAMIENTO EN MEMORIA EFICIENTE (useMemo) ---
    const sortedColaboradores = useMemo(() => {
        return [...(dbMasters?.colaboradores || [])].sort((a, b) => 
            (a.nombre || '').localeCompare(b.nombre || '')
        );
    }, [dbMasters?.colaboradores]);

    // Helper interno para el toggle de postventa ('Si'/'No')
    const handlePostventaToggle = (checked) => {
        setFormData(prev => ({ 
            ...prev, 
            postventa: checked ? 'Si' : 'No' 
        }));
    };

    return (
        <div className="space-y-8 animate-fade-in transition-all duration-300">
            
            {/* SECCIÓN DESPACHO */}
            <fieldset className="border border-slate-200 rounded-xl p-6 bg-white shadow-sm text-left">
                <legend className="px-3 text-lg font-bold text-gray-700 tracking-tight">Despacho de Póliza</legend>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
                    <div>
                        <label className="label-form text-slate-500 font-bold mb-1 block">Fecha de Despacho</label>
                        <input 
                            type="date" 
                            name="fechaDespacho" 
                            value={formData.fechaDespacho || ''} 
                            onChange={handleChange} 
                            className="input-round border-slate-200 focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all duration-200 text-left" 
                        />
                    </div>
                    <div>
                        <label className="label-form text-slate-500 font-bold mb-1 block">Forma de Despacho</label>
                        <select 
                            name="formaDespacho" 
                            value={formData.formaDespacho || ''} 
                            onChange={handleChange} 
                            className="input-round border-slate-200 focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all duration-200 text-left"
                        >
                            <option value="">Seleccione...</option>
                            {(formaDespachoList || []).map((f, i) => (
                                <option key={i} value={f}>{f}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </fieldset>

            {/* SECCIÓN INFORMACIÓN ADICIONAL */}
            <fieldset className="border border-slate-200 rounded-xl p-6 bg-white shadow-sm text-left">
                <legend className="px-3 text-lg font-bold text-gray-700 tracking-tight">Información Adicional</legend>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2 items-start">
                    
                    {/* FECHA REFACTURACIÓN */}
                    <div>
                        <label className="label-form text-slate-500 font-bold mb-1 block">Fecha Próxima Refacturación</label>
                        <input 
                            type="date" 
                            name="fechaRefacturacion" 
                            value={formData.fechaRefacturacion || ''} 
                            onChange={handleChange} 
                            className="input-round border-slate-200 focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all duration-200 text-left" 
                        />
                    </div>

                    {/* GESTIÓN POST VENTA */}
                    <div className={formData.postventa === 'Si' ? 'md:col-span-2' : ''}>
                        <label className="label-form text-slate-500 font-bold mb-1 block">Seguimiento</label>
                        <label className={`flex items-center justify-between cursor-pointer p-3 rounded-xl border transition-all duration-300 h-[46px] ${
                            formData.postventa === 'Si' ? 'bg-blue-50 border-blue-200 mb-4 ring-1 ring-blue-100' : 'bg-white border-slate-200 hover:bg-slate-50'
                        }`}>
                            <div className="flex items-center">
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors duration-300 ${
                                    formData.postventa === 'Si' ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-300'
                                }`}>
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                                <span className={`ml-3 text-xs font-bold uppercase tracking-tight transition-colors duration-300 ${
                                    formData.postventa === 'Si' ? 'text-blue-700' : 'text-slate-400'
                                }`}>
                                    Gestión Post Venta
                                </span>
                            </div>
                            <input 
                                type="checkbox" 
                                checked={formData.postventa === 'Si'} 
                                onChange={(e) => handlePostventaToggle(e.target.checked)} 
                                className="sr-only peer" 
                            />
                            <div className="w-10 h-5 bg-slate-200 rounded-full relative transition-colors duration-300 peer-checked:bg-blue-500 after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-white after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:after:translate-x-[20px] shadow-sm"></div>
                        </label>

                        {/* DESPLEGABLE DE PRIMERA GESTIÓN */}
                        {formData.postventa === 'Si' && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 p-5 bg-blue-50/50 border border-blue-100 rounded-xl animate-fade-in shadow-inner">
                                <div className="md:col-span-2 border-b border-blue-100 pb-2 mb-1 flex items-center gap-2 select-none">
                                    <svg className="w-4 h-4 text-blue-600 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                                    </svg>
                                    <h4 className="text-sm font-bold text-blue-800 uppercase tracking-tight">Registro Inicial de Seguimiento</h4>
                                </div>
                                
                                <div>
                                    <label className="label-form text-blue-700 font-bold mb-1 block">Etapa / Acción Realizada</label>
                                    <select 
                                        name="pv_tipoGestion" 
                                        value={formData.pv_tipoGestion || ''} 
                                        onChange={handleChange} 
                                        className="input-round border-blue-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-100 bg-white font-medium text-slate-700"
                                    >
                                        <option value="">Seleccione etapa...</option>
                                        {(etapasPostVentaList || []).map(et => <option key={et} value={et}>{et}</option>)}
                                    </select>
                                </div>

                                <div>
                                    <label className="label-form text-blue-700 font-bold mb-1 block">¿Quién gestionó?</label>
                                    <select 
                                        name="pv_quienGestiono" 
                                        value={formData.pv_quienGestiono || ''} 
                                        onChange={handleChange} 
                                        className="input-round border-blue-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-100 bg-white font-semibold text-slate-700"
                                    >
                                        <option value="">Seleccione colaborador...</option>
                                        {sortedColaboradores.map(c => (
                                            <option key={c.id || c.nombre} value={c.nombre}>
                                                {c.nombre}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="md:col-span-2 text-left">
                                    <label className="label-form text-blue-700 font-bold mb-1 block">Observaciones</label>
                                    <textarea 
                                        name="pv_comentarios" 
                                        value={formData.pv_comentarios || ''} 
                                        onChange={handleChange} 
                                        className="input-round h-auto py-3 min-h-[80px] border-blue-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-100 bg-white resize-none text-sm text-slate-700 font-medium"
                                        placeholder="Detalle de la gestión..."
                                    />
                                </div>

                                <div>
                                    <label className="label-form text-blue-700 font-bold mb-1 block">Próxima Gestión <span className="text-blue-400 font-normal normal-case">(Opcional)</span></label>
                                    <input 
                                        type="date" 
                                        name="pv_fechaProximaGestion" 
                                        value={formData.pv_fechaProximaGestion || ''} 
                                        onChange={handleChange} 
                                        className="input-round border-blue-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-100 bg-white text-slate-700 font-bold" 
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </fieldset>
        </div>
    );
};

export default SectionAdicional;